/** Shared onboarding module key merge + display helpers. */

const MODULE_KEY_ALIASES: Record<string, string> = {
  speed: 'sailing',
  sailing: 'sailing',
  'sailing-performance': 'sailing',
  cargo: 'cargo-heating',
  'cargo-heating': 'cargo-heating',
  'cargo_heating': 'cargo-heating',
  emission: 'emission',
  port: 'port',
  'port-consumption': 'port',
  'port_consumption': 'port',
};

const MODULE_LABELS: Record<string, string> = {
  'cargo-heating': 'Cargo Heating',
  emission: 'Emission',
  sailing: 'Sailing Performance',
  port: 'Port Performance',
};

export type OnboardingModuleBadgeVariant = 'cargo' | 'emission' | 'default';

export function normalizeOnboardingModuleKey(value: unknown): string | null {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) {
    return null;
  }
  return MODULE_KEY_ALIASES[raw] ?? raw;
}

export function mergeOnboardingModules(
  ...sources: unknown[]
): string[] {
  const merged: string[] = [];

  const append = (value: unknown): void => {
    const key = normalizeOnboardingModuleKey(value);
    if (key && !merged.includes(key)) {
      merged.push(key);
    }
  };

  const walk = (source: unknown): void => {
    if (source == null) {
      return;
    }
    if (Array.isArray(source)) {
      source.forEach(walk);
      return;
    }
    if (typeof source === 'string') {
      source
        .split(/[,;|]/)
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach(append);
      return;
    }
    if (typeof source === 'object') {
      const record = source as Record<string, unknown>;
      walk(record['onboarding_modules'] ?? record['onboardingModules']);
      if (record['onboarding_data'] && typeof record['onboarding_data'] === 'object') {
        walk(record['onboarding_data']);
      }
    }
  };

  sources.forEach(walk);
  return merged;
}

export function formatOnboardingModuleLabel(moduleKey: string): string {
  const normalized = normalizeOnboardingModuleKey(moduleKey);
  if (!normalized) {
    return '';
  }
  if (MODULE_LABELS[normalized]) {
    return MODULE_LABELS[normalized];
  }
  return normalized
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function getOnboardingModuleBadgeVariant(
  moduleKey: string,
): OnboardingModuleBadgeVariant {
  const normalized = normalizeOnboardingModuleKey(moduleKey);
  if (normalized === 'cargo-heating') {
    return 'cargo';
  }
  if (normalized === 'emission') {
    return 'emission';
  }
  return 'default';
}

export function getOnboardingModuleBadgeClass(moduleKey: string): string {
  return `onboarding-module-badge--${getOnboardingModuleBadgeVariant(moduleKey)}`;
}

export function extractOnboardingModulesFromRow(
  row: Record<string, unknown> | null | undefined,
): string[] {
  if (!row) {
    return [];
  }
  return mergeOnboardingModules(row);
}

export function enrichRowWithMergedOnboardingModules(
  imo: string,
  detail: Record<string, unknown>,
  options: {
    emissionVessels?: Array<Record<string, unknown>>;
    cargoHeatingVessels?: Array<Record<string, unknown>>;
    additionalSources?: unknown[];
    ensureModules?: string[];
  } = {},
): Record<string, unknown> {
  const normalizedImo = String(imo ?? '').replace(/\D/g, '').trim();
  const inferred: string[] = [];

  const imoMatches = (vessel: Record<string, unknown>): boolean => {
    const vesselImo = String(
      vessel['imo'] ??
        vessel['vessel_imo'] ??
        vessel['imo_number'] ??
        '',
    )
      .replace(/\D/g, '')
      .trim();
    return !!normalizedImo && vesselImo === normalizedImo;
  };

  if (options.cargoHeatingVessels?.some(imoMatches)) {
    inferred.push('cargo-heating');
  }
  if (options.emissionVessels?.some(imoMatches)) {
    inferred.push('emission');
  }

  const merged = mergeOnboardingModules(
    detail,
    ...(options.additionalSources ?? []),
    inferred,
    ...(options.ensureModules ?? []),
  );

  return {
    ...detail,
    onboarding_modules: merged,
    onboardingModules: merged,
  };
}
